import { createIntl, createIntlCache } from '@formatjs/intl';
import { createAsync } from '@solidjs/router';
import { createContext, createEffect, createMemo, createSignal, Show, Suspense, untrack, useContext } from 'solid-js';
import type { IntlShape, MessageDescriptor, OnErrorFn, OnWarnFn } from '@formatjs/intl';
import type { Options as IntlMessageFormatOptions } from 'intl-messageformat';
import type { Accessor, ParentProps, JSXElement } from 'solid-js';

const IntlContext = createContext<
	Accessor<
		IntlShape & {
			t: (
				descriptor: MessageDescriptor,
				values?: Record<string, number | string | undefined | boolean | Date | ((str: string) => JSXElement)>,
				opts?: IntlMessageFormatOptions,
			) => string;
			collator: Intl.Collator;
		}
	>
>();

export type Units = 'imperial' | 'metric';

const LocaleContext = createContext<{
	locale: Accessor<string>;
	setLocale: (locale: string) => void;
	units: Accessor<Units>;
	setUnits: (units: Units) => void;
	fetcher: (opts: { key: string; locale: string }) => Promise<Record<string, string>>;
}>();

const cache = createIntlCache();

type Props<K extends string = string, L extends string = string> = ParentProps<{
	fetcher: (opts: { key: K; locale: L }) => Promise<Record<string, string>>;
	key: K;
	locale: L;
	units?: Units;
	onWarn?: OnWarnFn;
	onError?: OnErrorFn;
	timezone: string;
}>;

export function IntlProvider(props: Props) {
	const ctx = useContext(LocaleContext);
	const fetcher = () => ctx?.fetcher ?? props.fetcher;
	// oxlint-disable-next-line solid/reactivity
	const [locale, setLocale] = ctx ? [ctx.locale, ctx.setLocale] : createSignal(untrack(() => props.locale));
	const [units, setUnits] = ctx
		? [ctx.units, ctx.setUnits]
		: // oxlint-disable-next-line solid/reactivity
			createSignal<Units>(untrack(() => props.units ?? 'imperial'));

	createEffect(() => {
		setLocale(props.locale);
	});
	createEffect(() => {
		setUnits(props.units ?? 'imperial');
	});

	const messages = createAsync(
		async () => {
			if (!fetcher()) {
				throw new Error('No i18n fetcher found');
			}
			return Object.entries(await fetcher()({ key: props.key, locale: locale() })).reduce<Record<string, string>>(
				(memo, [key, value]) => {
					// memo[`${props.key}.${key}`] = value;
					memo[key] = value;
					return memo;
				},
				{},
			);
		},
		{ deferStream: true },
	);

	return (
		<LocaleContext.Provider value={{ locale, setLocale, units, setUnits, fetcher: untrack(fetcher) }}>
			<Suspense>
				<Show when={messages()}>
					<Internal
						locale={locale}
						setLocale={setLocale}
						messages={messages()!}
						onWarn={props.onWarn}
						onError={props.onError}
						timezone={props.timezone}
					>
						{props.children}
					</Internal>
				</Show>
			</Suspense>
		</LocaleContext.Provider>
	);
}

function Internal(
	props: ParentProps<{
		locale: Accessor<string>;
		setLocale: (locale: string) => void;
		messages: Record<string, string>;
		onWarn?: OnWarnFn;
		onError?: OnErrorFn;
		timezone: string;
	}>,
) {
	const ctx = useContext(IntlContext);

	const intl = createMemo(() => {
		const intl = createIntl(
			{
				locale: props.locale(),
				defaultLocale: 'en',
				messages: { ...(ctx && (ctx().messages as Record<string, string>)), ...props.messages },
				onWarn: props.onWarn,
				onError: props.onError,
				timeZone: props.timezone,
			},
			cache,
		);

		return {
			...intl,
			t: intl.formatMessage,
			collator: new Intl.Collator(intl.locale),
		};
	});

	return <IntlContext.Provider value={intl}>{props.children}</IntlContext.Provider>;
}

export function useLocale() {
	const ctx = useContext(LocaleContext);
	if (!ctx) {
		throw new Error('LocaleProvider not configured');
	}
	return [ctx.locale, ctx.setLocale] as [Accessor<string>, (locale: string) => void];
}

export function useUnits() {
	const ctx = useContext(LocaleContext);
	if (!ctx) {
		throw new Error('LocaleProvider not configured');
	}
	return [ctx.units, ctx.setUnits] as [Accessor<Units>, (units: Units) => void];
}

export function useIntl() {
	const ctx = useContext(IntlContext);
	if (!ctx) {
		throw new Error('IntlProvider not configured');
	}
	return ctx;
}
